import React, { useCallback, useEffect, useState } from "react";
import AdminHeader from "./Header";
import "../../styles/Super.css";
import { useNotification } from "../../Toast/ToastProvider";
import { useNavigate, useParams } from "react-router-dom";
import {
  formatMonthDateYear,
  formatShortMonthDateYearWithTime,
  getAdmin,
} from "../../include/function";
import { adminReqConfig, api } from "../../include/api";
import { ChevronDown } from "react-feather";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ERROR, SUCCESS } from "../../include/constant";

const AdminOrderDetails = () => {
  const toast = useNotification();
  const { push } = useNavigate();
  const user = getAdmin();
  const { id } = useParams();

  const [order, setOrder] = useState(null); // single order object
  const [fetchLoading, setFetchLoading] = useState(true);
  const [customerDetail, setCustomerDetail] = useState([]);

  const getOrder = useCallback(() => {
    setFetchLoading(true);
    api
      .get(`/orders/?id=${id}`, adminReqConfig())
      .then((response) => {
        if (response.status === 200) {
          const orderData = response.data.data[0] || null;
          setOrder(orderData);
          if (orderData?.customerId) {
            getCustomerDetail(orderData?.customerId);
          }
        }
      })
      .catch((error) => {
        toast({ type: "error", message: "Failed to load order." });
        push("/admin/sales/orders");
      })
      .finally(() => {
        setFetchLoading(false);
      });
  }, [id, user.id, push, toast]);

  const getCustomerDetail = useCallback((customerId) => {
    if (!customerId) return;
    setFetchLoading(true);
    api
      .get(`/customer/?id=${customerId}`, adminReqConfig())
      .then((response) => {
        if (response.status === 200) {
          setCustomerDetail(response.data.data[0] || null);
        }
      })
      .catch((error) => {
        toast({ type: "error", message: "Failed to load customer detail." });
      })
      .finally(() => {
        setFetchLoading(false);
      });
  }, []);

  // Then in useEffect:
  useEffect(() => {
    getOrder();
  }, []);

  const convertToInvoice = () => {
    setFetchLoading(true);
    if (!order?.id || !order?.customerId) {
      toast({ type: ERROR, message: "OrderId and CustomerId required" });
      setFetchLoading(false);
      return;
    }

    const payload = {
      customerId: order.customerId,
      invoiceStatus: "yes",
      invoiceId: generateNumericId(), // Generate a numeric-only ID
    };

    api
      .put(
        `/order_for_super/?id=${order.id}&flag=changeInvoice`,
        payload,
        adminReqConfig(),
      )
      .then((response) => {
        if (response.status === 200) {
          toast({
            type: SUCCESS,
            message: "Successfully Convert into invoice",
          });
          getOrder(); // Refresh the order data after successful conversion
        } else {
          toast({ type: ERROR, message: "Failed to Convert into Invoice" });
        }
      })
      .catch((error) => {
        console.error(error);
        toast({ type: "error", message: "Error converting order to invoice." });
        setFetchLoading(false);
      });
  };

  // Helper function to generate a numeric-only ID
  const generateNumericId = () => {
    const chars = "0123456789";
    let id = "";
    for (let i = 0; i < 10; i++) {
      // Adjust length as needed
      id += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return id;
  };

  const handleDownloadPDF = () => {
    if (!order) return;

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // --- Title ---
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Order Details", 105, 15, { align: "center" });

    // --- Basic Info ---
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Order ID: #${formatOrderId(order)}`, 14, 25);
    doc.text(`Date: ${formatMonthDateYear(order.createTimestamp)}`, 150, 25);

    // --- Customer Info ---
    doc.setFont("helvetica", "bold");
    doc.text("Customer Details:", 14, 35);
    doc.setFont("helvetica", "normal");
    doc.text(`Name: ${order.name || "N/A"}`, 14, 40);
    doc.text(`Customer Type: ${order.customerType || "N/A"}`, 14, 45);
    doc.text(`Payment Type: ${order.paymentType || "N/A"}`, 14, 50);

    // --- Billing Address ---
    doc.setFont("helvetica", "bold");
    doc.text("Billing Address:", 14, 60);
    doc.setFont("helvetica", "normal");
    const billingAddress =
      order.billAddress ||
      order.billLandmark ||
      order.billLocality ||
      order.billCity ||
      order.billState ||
      order.billPin
        ? `${order.billAddress || ""}, ${order.billLandmark || ""}, ${
            order.billLocality || ""
          }, ${order.billCity || ""}, ${order.billState || ""} - ${
            order.billPin || ""
          }`
        : "N/A";
    doc.text(billingAddress, 14, 65, { maxWidth: 180 });

    // --- Delivery Address ---
    doc.setFont("helvetica", "bold");
    doc.text("Delivery Address:", 14, 75);
    doc.setFont("helvetica", "normal");
    const deliveryAddress =
      order.address ||
      order.landmark ||
      order.locality ||
      order.city ||
      order.state ||
      order.pin
        ? `${order.address || ""}, ${order.landmark || ""}, ${
            order.locality || ""
          }, ${order.city || ""}, ${order.state || ""} - ${order.pin || ""}`
        : "N/A";
    doc.text(deliveryAddress, 14, 80, { maxWidth: 180 });

    const tableBody = [];

    // --- Product Rows ---
    if (order.orderItems?.length > 0) {
      order.orderItems.forEach((item, i) => {
        const price = Number(item.price || 0);
        const qty = Number(item.qty || 0);
        const discount = Number(item.discountValue || 0);
        const productAmount = (price * qty).toFixed(2);
        const amount = (productAmount - discount).toFixed(2);

        tableBody.push([
          i + 1,
         `${item.productName || "N/A"} ${item.variant !== "" ? "(" : ""} ${item.variant ? item.variant : ""} ${item.variant !== "" ? ")" : ""}`,
          qty,
          `${price.toFixed(2)}`,
          `${discount}`,
          `${amount}`,
        ]);
      });
    }

    tableBody.push(
      [
        { content: "Coupon Discount", colSpan: 5, styles: { halign: "right" } },
        { content: `-${Number(order.voucherDiscountAmount || 0).toFixed(2)}` },
      ],
      [
        {
          content: "Delivery Charges",
          colSpan: 5,
          styles: { halign: "right" },
        },
        { content: `${Number(order.totalDeliveryCharge || 0).toFixed(2)}` },
      ],
      [
        {
          content: "Total Amount",
          colSpan: 5,
          styles: { halign: "right", fontStyle: "bold" },
        },
        {
          content: `${Number(order.billAmount || 0).toFixed(2)}`,
          styles: { fontStyle: "bold" },
        },
      ],
    );

    // --- Table Render ---
    autoTable(doc, {
      startY: 90,
      head: [["S.No", "Product", "Qty", "Rate", "Discount", "Amount"]],
      body: tableBody,
      theme: "grid",
      styles: {
        fontSize: 9,
        cellPadding: 2,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
    });

    // --- Footer ---
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.text("Thank you for shopping with us!", 105, pageHeight - 10, {
      align: "center",
    });

    doc.save(`Order_${formatOrderId(order)}.pdf`);
  };

 
  //Dropdown options
  const PAYMENT_OPTIONS = [
    { value: "paid", label: "Paid" },
    { value: "pending", label: "Unpaid" },
    { value: "partially paid", label: "Partially Paid" },
  ];

  const APPROVAL_OPTIONS = [
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ];

  const STATUS_OPTIONS = [
    { value: "ordered", label: "Recieved" },
    { value: "confirmed", label: "Confirmed" },
    { value: "packed", label: "Packed" },
    { value: "shipped", label: "Shipped" },
    { value: "delivered", label: "Delivered" },
    { value: "return", label: "Return" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const totalQuantity = order?.orderItems?.reduce(
    (sum, item) => sum + Number(item.qty || 0),
    0,
  );

  const formatOrderId = (order) => {
    if (!order?.createTimestamp || !order?.id) return "ORD--";

    // Extract last 2 digits of year
    const year = new Date(order.createTimestamp).getFullYear();
    const yy = String(year).slice(-2); // e.g., 2025 → "25"

    // Zero-pad order.id to 4 digits
    const paddedId = String(order.id).padStart(4, "0"); // e.g., 7 → "0007"

    return `ORD${yy}-${paddedId}`;
  };

  const getDaysCrossed = (timestamp) => {
    if (!timestamp) return 0;

    const orderDate = new Date(timestamp);
    const now = new Date();

    const diffTime = now.getTime() - orderDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return diffDays >= 0 ? diffDays : 0;
  };

  return (
    <AdminHeader title="ORDER DETAILS">
      <div className="content-wrapper p-0">
        <section
          style={{
            backgroundColor: "#F1F4F9",
            padding: "30px",
            marginTop: "70px",
            marginBottom: "40px",
            minHeight: "80vh",
          }}
        >
          {/* Middle Row: Customer Info + Address + Date */}
          <div className="row g-3 mb-10">
            <div className="col-8">
              <div
                className="order-detail-card shadow-sm"
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  padding: "20px",
                }}
              >
                <div
                  className=" mb-3"
                  style={{
                    backgroundColor: "#fff",
                    display: "flex",
                    // justifyContent: "",
                    alignItems: "center",
                    // flexWrap: "wrap",
                    gap: "16px",
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <h6
                      style={{
                        fontWeight: "700",
                        fontSize: "18px",
                        color: "#2c3e50",
                        margin: 0,
                      }}
                    >
                      Order ID{" "}
                      <span style={{ color: "#9da6b5" }}>
                        {formatOrderId(order)}
                      </span>
                    </h6>
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <CustomColoredDropdown
                      value={order?.orderItems[0].status}
                      options={STATUS_OPTIONS}
                      type="status"
                      disable={true}
                    />
                    <CustomColoredDropdown
                      value={order?.paymentStatus}
                      options={PAYMENT_OPTIONS}
                      type="payment"
                      disable={true}
                    />
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      onClick={convertToInvoice}
                      style={{
                        color: "#fff",
                        backgroundColor: "#2b95d3",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: "700",
                        cursor:
                          order?.invoiceStatus === "yes"
                            ? "not-allowed"
                            : "pointer",
                        padding: "8px 16px",
                        minWidth: "150px",
                        height: "40px",
                        border: "none",
                        textWrap: "nowrap",
                      }}
                      fetchLoading={"lading..."}
                      disabled={order?.invoiceStatus === "yes"}
                    >
                      {order?.invoiceStatus === "yes"
                        ? "Already Converted"
                        : " Convert To Invoice"}
                    </button>

                    <button
                      onClick={handleDownloadPDF}
                      style={{
                        color: "#2692d2",
                        backgroundColor: "#d9ebff",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: "700",
                        cursor: "pointer",
                        padding: "8px 16px",
                        minWidth: "150px",
                        height: "40px",
                        border: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                      }}
                    >
                      <img
                        src="/assets/images/pdf-dow.png"
                        alt="PDF"
                        style={{ height: "16px", width: "16px" }}
                      />
                      PDF
                    </button>
                  </div>
                </div>
                <div className="row g-4 mt-14">
                  <div className="col-3">
                    <div style={{ fontSize: "14px", color: "black" }}>
                      <strong>Customer Name</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {order?.name || "N/A"}
                      </p>
                    </div>

                    <div
                      style={{
                        fontSize: "14px",
                        color: "black",
                        marginTop: "30px",
                      }}
                    >
                      <strong>Billing Address</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                          lineHeight: "1.4",
                        }}
                      >
                        {order?.billAddress ||
                        order?.billLandmark ||
                        order?.billLocality ||
                        order?.billCity ||
                        order?.billState ||
                        order?.billPin ? (
                          <>
                            {order?.billAddress && `${order?.billAddress}, `}
                            {order?.billLandmark && `${order?.billLandmark}, `}
                            {order?.billLocality && `${order?.billLocality}, `}
                            {order?.billCity && `${order?.billCity}, `}
                            <br />
                            {order?.billState && `${order?.billState}-`}
                            {order?.billPin}
                          </>
                        ) : (
                          "N/A"
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="col-3">
                    <div style={{ fontSize: "14px", color: "black" }}>
                      <strong>Ordered Date & Time</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {formatShortMonthDateYearWithTime(
                          order?.createTimestamp,
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="col-3">
                    <div
                      style={{
                        fontSize: "14px",
                        color: "black",
                      }}
                    >
                      <strong>Payment Type</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {order?.orderItems?.[0].paymentMode || "N/A"}
                      </p>
                    </div>

                    <div
                      style={{
                        fontSize: "14px",
                        color: "black",
                        marginTop: "30px",
                      }}
                    >
                      <strong>Delivery Address</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                          lineHeight: "1.4",
                        }}
                      >
                        {order?.address},{order?.landmark},{order?.locality},
                        {order?.city},<br />
                        {order?.state}-{order?.pin}
                      </p>
                    </div>
                  </div>

                  <div className="col-3">
                    <div
                      style={{
                        fontSize: "14px",
                        color: "black",
                      }}
                    >
                      <strong>Billing Name</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {order?.billName || "N/A"}
                      </p>
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "black",
                        marginTop:'30px'
                      }}
                    >
                      <strong>Billing GST Number</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {order?.billGst || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-4">
              <div
                className="order-detail-card shadow-sm mb-3"
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  padding: "20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "12px",
                  }}
                >
                  <h6
                    style={{
                      fontWeight: "700",
                      fontSize: "18px",
                      color: "#2c3e50",
                      margin: 0,
                    }}
                  >
                    Total Amount
                  </h6>
                </div>
                <p
                  style={{
                    background: "#d9ebff",
                    padding: "10px",
                    borderRadius: "5px",
                    fontWeight: "600",
                    color: "#6E7C95",
                    margin: 0,
                  }}
                >
                  ₹{" "}
                  {Number(order?.billAmount || 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
              </div>

              <div
                className="order-detail-card shadow-sm"
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  padding: "20px",
                  marginTop: "24px",
                }}
              >
                <h6
                  style={{
                    fontWeight: "700",
                    fontSize: "18px",
                    color: "#2c3e50",
                    marginBottom: "18px",
                  }}
                >
                  Total Quantity
                </h6>
                <p
                  style={{
                    background: "#d9ebff",
                    padding: "10px",
                    borderRadius: "5px",
                    fontWeight: "600",
                    color: "#6E7C95",
                    margin: 0,
                  }}
                >
                  {totalQuantity}
                </p>
              </div>
            </div>
          </div>

          {/* Customer Details */}
          <div className="row g-3 mb-3 ">
            <div className="col-8">
              <div
                className="order-detail-card shadow-sm"
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  padding: "20px",
                }}
              >
                <div className="d-flex align-items-center gap-3">
                  <h6
                    style={{
                      fontWeight: "700",
                      fontSize: "18px",
                      color: "#2c3e50",
                      margin: 0,
                    }}
                  >
                    Customer Details
                  </h6>
                </div>
                <div className="row g-4 mt-14">
                  <div className="col-3">
                    <div style={{ fontSize: "14px", color: "black" }}>
                      <strong>Customer Name</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {customerDetail?.name || "Riyas Ahamed"}
                      </p>
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "black",
                        marginTop: "30px",
                      }}
                    >
                      <strong>Phone Number</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {customerDetail?.phoneNumber || "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="col-3">
                    <div style={{ fontSize: "14px", color: "black" }}>
                      <strong>Customer Type</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {customerDetail.customerType
                          ? `${customerDetail.customerType} (${
                              customerDetail.customerType === "individual"
                                ? "B2C"
                                : "B2B"
                            })`
                          : "N/A"}{" "}
                      </p>
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "black",
                        marginTop: "30px",
                      }}
                    >
                      <strong>Email ID</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {customerDetail?.emailId || "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="col-3">
                    <div style={{ fontSize: "14px", color: "black" }}>
                      <strong>Company Name</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {customerDetail?.shopName || "N/A"}
                      </p>
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "black",
                        marginTop: "30px",
                      }}
                    >
                      <strong>Address</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                          lineHeight: "1.4",
                        }}
                      >
                        {customerDetail?.address1 || "N/A"},
                        {customerDetail?.address2 || "."}
                      </p>
                    </div>
                  </div>
                  <div className="col-3">
                    <div style={{ fontSize: "14px", color: "black" }}>
                      <strong>GST Number</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {customerDetail?.proof?.[0]?.gstNo || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {order?.orderItems[0]?.paymentMode === "credit" ? (
              <div className="col-4">
                <div
                  className="order-detail-card shadow-sm mb-3"
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    padding: "20px",
                  }}
                >
                  {/* Credit Balance */}
                  <div className="">
                    <h6
                      style={{
                        fontWeight: "700",
                        fontSize: "18px",
                        color: "#2c3e50",
                        marginBottom: "12px",
                      }}
                    >
                      Credit Balance
                    </h6>
                    {order?.paymentStatus === "paid" ? (
                      <>
                        <div
                          style={{
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                            borderRadius: "4px",
                            overflow: "hidden",
                            marginBottom: "8px",
                          }}
                        >
                          <div
                            style={{
                              width: "0%",
                              height: "100%",
                              backgroundColor: "#3498db",
                              borderRadius: "4px",
                            }}
                          ></div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#6E7C94",
                              fontWeight: "500",
                            }}
                          >
                            Nil
                          </span>
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#6E7C94",
                              fontWeight: "500",
                            }}
                          >
                            Nil
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        {(() => {
                          const currentCredit =
                            Number(customerDetail?.currentCredit) || 0;
                          const creditBal =
                            Number(customerDetail?.creditBal) || 0;

                          // 🔒 Safe percentage calculation
                          const percentage =
                            creditBal > 0
                              ? Math.min((currentCredit / creditBal) * 100, 100)
                              : 0;

                          const isOverLimit =
                            currentCredit > creditBal && creditBal > 0;

                          return (
                            <>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "10px",
                                  marginBottom: "8px",
                                }}
                              >
                                <div
                                  style={{
                                    flex: 1,
                                    height: "12px",
                                    backgroundColor: "#e0e0e0",
                                    borderRadius: "4px",
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: `${percentage}%`,
                                      height: "100%",
                                      backgroundColor: isOverLimit
                                        ? "#D32B2E"
                                        : "#3498db",
                                      borderRadius: "4px",
                                      transition: "width 0.3s ease",
                                    }}
                                  ></div>
                                </div>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "14px",
                                    color: isOverLimit ? "#D32B2E" : "#6E7C94",
                                    fontWeight: "500",
                                  }}
                                >
                                  ₹{currentCredit.toLocaleString()} used{" "}
                                  {isOverLimit && "Exceeds limit"}
                                </span>
                                {!isOverLimit && creditBal > 0 ? (
                                  <span
                                    style={{
                                      fontSize: "14px",
                                      color: "#6E7C94",
                                      fontWeight: "500",
                                    }}
                                  >
                                    out of ₹{creditBal.toLocaleString()}
                                  </span>
                                ) : creditBal === 0 ? (
                                  <span
                                    style={{
                                      fontSize: "14px",
                                      color: "#6E7C94",
                                      fontWeight: "500",
                                    }}
                                  >
                                    out of ₹0
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      fontSize: "14px",
                                      color: "#6E7C94",
                                      fontWeight: "500",
                                    }}
                                  >
                                    out of ₹{creditBal.toLocaleString()}
                                  </span>
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </>
                    )}
                  </div>
                  {/* Credit Limit (In Days) */}
                  <div className="mt-24 mb-12">
                    <h6
                      style={{
                        fontWeight: "700",
                        fontSize: "18px",
                        color: "#2c3e50",
                        marginBottom: "12px",
                      }}
                    >
                      Credit Limit (In Days)
                    </h6>
                    {order?.paymentStatus === "paid" ? (
                      <>
                        <div
                          style={{
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                            borderRadius: "4px",
                            overflow: "hidden",
                            marginBottom: "8px",
                          }}
                        >
                          <div
                            style={{
                              width: "0%",
                              height: "100%",
                              backgroundColor: "#3498db",
                              borderRadius: "4px",
                            }}
                          ></div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#6E7C94",
                              fontWeight: "500",
                            }}
                          >
                            Nil
                          </span>
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#6E7C94",
                              fontWeight: "500",
                            }}
                          >
                            Nil
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        {(() => {
                          const daysCrossed = getDaysCrossed(
                            customerDetail?.creditDate,
                          );
                          const creditLimitDays =
                            Number(customerDetail?.creditLimit) || 0;
                          const isOverLimit = daysCrossed > creditLimitDays;

                          return (
                            <>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "10px",
                                  marginBottom: "8px",
                                }}
                              >
                                <div
                                  style={{
                                    flex: 1,
                                    height: "12px",
                                    backgroundColor: "#e0e0e0",
                                    borderRadius: "4px",
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: `${Math.min(
                                        (daysCrossed / creditLimitDays) * 100,
                                        100,
                                      )}%`,
                                      height: "100%",
                                      backgroundColor: isOverLimit
                                        ? "#D32B2E"
                                        : "#3498db", // Red if over
                                      borderRadius: "4px",
                                    }}
                                  ></div>
                                </div>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "14px",
                                    color: isOverLimit ? "#D32B2E" : "#6E7C94",
                                    fontWeight: "500",
                                  }}
                                >
                                  {isOverLimit
                                    ? `${daysCrossed} days Exceeds Limit`
                                    : `${daysCrossed} days crossed`}
                                </span>
                                <span
                                  style={{
                                    fontSize: "14px",
                                    color: "#6E7C94",
                                    fontWeight: "500",
                                  }}
                                >
                                  out of {creditLimitDays} days
                                </span>
                              </div>
                            </>
                          );
                        })()}
                      </>
                    )}
                  </div>

                  {/* Footer message: show if either balance or days are exceeded */}
                  {(() => {
                    const currentCredit =
                      Number(customerDetail?.currentCredit) || 0;
                    const creditBal = Number(customerDetail?.creditBal) || 0;
                    const daysCrossed = getDaysCrossed(
                      customerDetail?.creditDate,
                    );
                    const creditLimitDays =
                      Number(customerDetail?.creditLimit) || 10;
                    const isOverBalance = currentCredit > creditBal;
                    const isOverDays = daysCrossed > creditLimitDays;

                    if (isOverBalance || isOverDays) {
                      return (
                        <p
                          style={{
                            marginTop: "16px",
                            fontSize: "13px",
                            color: "#6E7C94",
                            fontWeight: "",
                          }}
                        >
                          Limit will reset after total outstanding is cleared.
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            ) : (
              <div className="col-md-4"></div>
            )}
          </div>

          <div className="rounded-5 bg-white mt-24">
            <h6
              style={{
                fontWeight: "700",
                fontSize: "18px",
                color: "#2c3e50",
                marginBottom: "16px",
                padding: "20px",
              }}
            >
              Ordered Products
            </h6>

            <div className="table-responsive mt-24 order-table">
              <table className="table table-striped custom-striped-table-order-detail align-middle ">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Order ID</th>
                    <th>Product</th>
                    <th>Quantity</th>
                    <th>Price</th>
                    <th>Selling Price</th>
                    <th>Discount Rate</th>
                    <th>Status</th>
                    <th>Amount</th>
                  </tr>
                </thead>

                <tbody>
                  {order?.orderItems && order.orderItems.length > 0 ? (
                    order.orderItems.map((item, index) => {
                      const unitPrice = Number(item.price || 0);
                      const qty = Number(item.qty || 0);
                      const discountRate = Number(item.discountValue || 0);
                      const discountValue = parseFloat(item.discountValue || 0);
                      let sellingPrice = unitPrice;

                      if (item.discountMode === "percent") {
                        sellingPrice =
                          unitPrice - (unitPrice * discountValue) / 100;
                      } else {
                        sellingPrice = unitPrice - discountValue;
                      }
                      sellingPrice = Math.max(sellingPrice, 0);

                      return (
                        <tr key={item.id || index}>
                          <td
                            style={{
                              paddingTop: "14px",
                              paddingBottom: "14px",
                            }}
                          >
                            {String(index + 1).padStart(2, "0")}
                          </td>
                          <td
                            style={{
                              paddingTop: "14px",
                              paddingBottom: "14px",
                            }}
                          >
                            {formatOrderId(order)}
                          </td>
                          <td
                            style={{
                              paddingTop: "14px",
                              paddingBottom: "14px",
                            }}
                          >
                            {item.productName || "N/A"}
                            {item.variant !== "" ? "(" : ""}
                            {item.variant ? item.variant : ""}
                            {item.variant !== "" ? ")" : ""}
                          </td>
                          <td
                            style={{
                              paddingTop: "14px",
                              paddingBottom: "14px",
                            }}
                          >
                            {qty} {item.unit || ""}
                          </td>
                          <td
                            style={{
                              paddingTop: "14px",
                              paddingBottom: "14px",
                            }}
                          >
                            ₹
                            {unitPrice.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </td>
                          <td>
                            ₹
                            {sellingPrice.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}{" "}
                          </td>
                          <td
                            style={{
                              paddingTop: "14px",
                              paddingBottom: "14px",
                            }}
                          >
                            {discountRate}
                          </td>
                          <td>
                            <CustomColoredDropdown
                              value={item.status}
                              options={STATUS_OPTIONS}
                              type="status"
                              disable={true}
                            />
                          </td>
                          <td
                            style={{
                              paddingTop: "14px",
                              paddingBottom: "14px",
                              textAlign: "right",
                            }}
                          >
                            ₹{item.amount}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7">No orders found.</td>
                    </tr>
                  )}
                </tbody>

                {/* Table Footer (Summary Rows) */}
                <tfoot>
                  <tr>
                    <td
                      colSpan="8"
                      className="custom-striped-table-product-list-tfoot-td"
                    >
                      Coupon Discount
                    </td>
                    <td
                      style={{
                        paddingTop: "14px",
                        paddingBottom: "14px",
                        paddingLeft: "16px",
                        paddingRight: "16px",
                        fontWeight: "600",
                        color: "#808ca2",
                        textAlign: "right",
                      }}
                    >
                      {" "}
                      -₹
                      {Number(order?.voucherDiscountAmount || 0).toLocaleString(
                        undefined,
                        {
                          minimumFractionDigits: 2,
                        },
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td
                      colSpan="8"
                      className="custom-striped-table-product-list-tfoot-td"
                      style={{ background: "#fff" }}
                    >
                      Delivery Charges
                    </td>
                    <td
                      style={{
                        paddingTop: "14px",
                        paddingBottom: "14px",
                        paddingLeft: "16px",
                        paddingRight: "16px",
                        fontWeight: "600",
                        color: "#808ca2",
                        background: "#fff",
                        textAlign: "right",
                      }}
                    >
                      ₹
                      {Number(order?.totalDeliveryCharge || 0).toLocaleString(
                        undefined,
                        {
                          minimumFractionDigits: 2,
                        },
                      )}
                    </td>
                  </tr>
                  <tr
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                    }}
                  >
                    <td
                      colSpan="8"
                      className="custom-striped-table-product-list-tfoot-td"
                    >
                      Total Amount
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        textAlign: "right",
                      }}
                    >
                      ₹
                      {Number(order?.billAmount || 0).toLocaleString(
                        undefined,
                        {
                          minimumFractionDigits: 2,
                        },
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </section>
      </div>
    </AdminHeader>
  );
};

export default AdminOrderDetails;

const CustomColoredDropdown = ({
  value,
  onChange,
  options,
  type = "payment",
  disable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const getColors = (status) => {
    if (type === "approval") {
      switch (status) {
        case "approved":
          return {
            color: "#065F46",
            bg: "#D1FAE5",
            border: `1px solid #065F46`,
          };
        case "rejected":
          return {
            color: "#991B1B",
            bg: "#FEE2E2",
            border: `1px solid #991B1B`,
          };
        default:
          return { color: "#CF9500", bg: "#FFF3BA", border: "none" };
      }
    }
    if (type === "payment") {
      switch (status) {
        case "paid":
          return { color: "white", bg: "#26DD8E", border: `1px solid #26DD8E` };
        case "pending":
          return { color: "white", bg: "#F8556D", border: `1px solid #F8556D` };
        case "partially-paid":
          return {
            color: "white",
            bg: "#D19A04",
            border: `1px solid #D19A04`,
          };
        default:
          return { color: "black", bg: "#fff", border: `1px solid #ccc` };
      }
    } else {
      switch (status) {
        case "delivered":
          return { color: "#46A27B", bg: "#D5F5E7", border: "none" };
        case "shipped":
          return { color: "#CF9500", bg: "#FFF3BA", border: "none" };
        case "packed":
          return { color: "#AC29AC", bg: "#FFCEFF", border: "none" };
        case "ordered":
          return { color: "#66B2E2", bg: "#D9EBFF", border: "none" };
        case "cancelled":
          return { color: "#E54611", bg: "#FDDFD0", border: "none" };
        case "return":
          return { color: "#8977C3", bg: "#DDD5F5", border: "none" };
        case "confirmed":
          return { color: "#39AFA7", bg: "#BFEFEC", border: "none" };
        case "rejected":
          return { color: "#E54611", bg: "#FDDFD0", border: "none" };
        case "need-approval":
          return { color: "#E4440E", bg: "#FDDFD0", border: "none" };
        default:
          return { color: "black", bg: "#f5f5f5", border: "none" };
      }
    }
  };
  const { color, bg, border } = getColors(value);
  const displayLabel =
    options.find((opt) => opt.value === value)?.label ||
    (value
      ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
      : value);

  // Only allow interaction if NOT disabled
  const handleToggle = () => {
    if (!disable) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (val) => {
    if (!disable) {
      onChange?.(val);
      setIsOpen(false);
    }
  };

  return (
    <div
      style={{
        position: "relative",
        display: "inline-block",
        fontSize: "14px",
      }}
    >
      {/* Main Button */}
      <div
        onClick={handleToggle}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "6px 12px",
          border: border,
          borderRadius: "6px",
          backgroundColor: bg,
          cursor: disable ? "default" : "pointer", // 👈 visual cue
          fontWeight: "500",
          color: color,
          minWidth: "100px",
          whiteSpace:'nowrap',
          opacity: disable ? 0.7 : 1, // optional: gray out when disabled
          pointerEvents: disable ? "none" : "auto", // 🔒 blocks all clicks if disabled
        }}
      >
        <span>{displayLabel}</span>
        {!disable && (
          <ChevronDown size={14} style={{ color, marginLeft: "6px" }} />
        )}
      </div>

      {/* Dropdown Menu — only show if enabled and open */}
      {!disable && isOpen && (
        <>
          <ul
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              width: "100%",
              listStyle: "none",
              margin: 0,
              padding: 0,
              backgroundColor: "#fff",
              border: border === "none" ? "none" : border,
              borderRadius: "6px",
              zIndex: 1000,
              boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              maxHeight: "150px",
              overflowY: "auto",
            }}
          >
            {options.map((opt) => {
              const optColors = getColors(opt.value);
              return (
                <li
                  key={opt.value}
                  onClick={() => handleOptionClick(opt.value)}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    color: optColors.color,
                    backgroundColor: optColors.bg,
                    fontWeight: "500",
                    border: "none",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#f0f0f0")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = optColors.bg)
                  }
                >
                  {opt.label}
                </li>
              );
            })}
          </ul>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
            }}
            onClick={() => setIsOpen(false)}
          />
        </>
      )}
    </div>
  );
};
